/**
 * Shared date utility functions for session date handling
 * Used by both upcoming and recently completed sessions selectors
 */

import { Session } from "../sessions/models/Session";
import { parseDateWithTimezone } from "./timezoneUtils";

/**
 * Interface for grouped sessions by date
 */
export interface GroupedSessions {
  dateLabel: string;
  dateDisplay: string;
  sessions: Session[]; // Generic to work with any session type
}



/**
 * Format a Date object to dd/mm/yyyy string format with optional timezone adjustment.
 * Example: Date for Nov 4, 2025 -> "04/11/2025"
 * With timezone: 22:00 GMT+2 on Nov 3 -> "04/11/2025" (shows next day due to timezone)
 * 
 * @param date - The date to format
 * @param timezoneOffsetMinutes - Optional timezone offset in minutes (positive for GMT+, negative for GMT-)
 *                               Example: GMT+2 = 120, GMT-5 = -300
 * @returns Formatted date string in dd/mm/yyyy format
 */
export function formatDateToDDMMYYYY(date: Date, timezoneOffsetMinutes?: number): string {
  let adjustedDate = date;
  
  if (timezoneOffsetMinutes !== undefined) {
    // Create a new date adjusted for the timezone offset
    const adjustedTime = date.getTime() + (timezoneOffsetMinutes * 60000);
    adjustedDate = new Date(adjustedTime);
  }
  
  const day = String(adjustedDate.getDate()).padStart(2, "0");
  const month = String(adjustedDate.getMonth() + 1).padStart(2, "0");
  const year = adjustedDate.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format date for display like "Oct 30"
 */
export function formatDateDisplay(date: Date): string {
  const monthName = date.toLocaleDateString("en-US", { month: "short" });
  const dayNumber = date.getDate();

  return `${monthName} ${dayNumber}`;
}

/**
 * Utility function to format date labels for recently completed sessions
 */
export function extractDateLabelAndDisplayDate(dateString: string): {
  label: string;
  display: string;
} {
  const date = parseDateWithTimezone(dateString);
  if (!date) {
    return { label: "Invalid", display: "Date" };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sessionDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  // Check if it's today
  if (sessionDate.getTime() === today.getTime()) {
    return {
      label: "Today",
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's tomorrow
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (sessionDate.getTime() === tomorrow.getTime()) {
    return {
      label: "Tomorrow",
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sessionDate.getTime() === yesterday.getTime()) {
    return {
      label: "Yesterday",
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's within the last week
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  if (sessionDate >= weekAgo) {
    return {
      label: date.toLocaleDateString("en-US", { weekday: "short" }),
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // For older dates, show the month abbreviation
  return {
    label: date.toLocaleDateString("en-US", { month: "short" }),
    display: date.toLocaleDateString("en-US", { day: "numeric" }),
  };
}

/**
 * Generic function to group sessions by date with customizable sorting
 */
export function groupSessionsByDate(
  sessions: Session[],
  maxSessions: number = 1,
  sortOrder: "asc" | "desc" = "asc"
): GroupedSessions[] {
  // Limit to max sessions
  const limitedSessions = sessions.slice(0, maxSessions);

  // Group sessions by date
  const groupedMap = new Map<string, GroupedSessions>();

  limitedSessions.forEach((session) => {
    const parsedDate = parseDateWithTimezone(session.dateStart);
    const dateKey = parsedDate
      ? formatDateToDDMMYYYY(parsedDate)
      : "Invalid Date";

    const { label, display } = extractDateLabelAndDisplayDate(session.dateStart);

    if (!groupedMap.has(dateKey)) {
      groupedMap.set(dateKey, {
        dateLabel: label,
        dateDisplay: display,
        sessions: [],
      });
    }

    groupedMap.get(dateKey)!.sessions.push(session);
  });

  // Convert map to array
  const groupedArray = Array.from(groupedMap.values());

  // Sort sessions within each date group by time using timeLocal property
  groupedArray.forEach((group) => {
    group.sessions.sort((a, b) => {
      // Convert timeLocal (HH:mm format) to minutes for comparison
      const [hoursA, minutesA] = a.timeLocal.split(':').map(Number);
      const [hoursB, minutesB] = b.timeLocal.split(':').map(Number);
      const timeMinutesA = hoursA * 60 + minutesA;
      const timeMinutesB = hoursB * 60 + minutesB;
      
      return sortOrder === "asc"
        ? timeMinutesA - timeMinutesB
        : timeMinutesB - timeMinutesA;
    });
  });

  // Sort date groups by date
  groupedArray.sort((a, b) => {
    const dateA = new Date(a.sessions[0].dateStart);
    const dateB = new Date(b.sessions[0].dateStart);
    return sortOrder === "asc"
      ? dateA.getTime() - dateB.getTime()
      : dateB.getTime() - dateA.getTime();
  });

  return groupedArray;
}
